"use client";

import { useEffect, useState } from "react";
import { signIn } from "next-auth/react";
import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import Link from "next/link";
import { toast } from "react-toastify";

// تابع تبدیل اعداد فارسی به انگلیسی
const persianToEnglishNumbers = (str: string) => {
  const persianNumbers = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  const englishNumbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
  
  let result = str;
  for (let i = 0; i < persianNumbers.length; i++) {
    result = result.replace(new RegExp(persianNumbers[i], 'g'), englishNumbers[i]);
  }
  return result;
};

// تابع تبدیل اعداد عربی به انگلیسی
const arabicToEnglishNumbers = (str: string) => {
  const arabicNumbers = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  const englishNumbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
  
  let result = str;
  for (let i = 0; i < arabicNumbers.length; i++) {
    result = result.replace(new RegExp(arabicNumbers[i], 'g'), englishNumbers[i]);
  }
  return result;
};

// تابع جامع تبدیل هر دو نوع عدد به انگلیسی
const convertToEnglishNumbers = (str: string) => {
  if (!str) return str;
  let result = persianToEnglishNumbers(str);
  result = arabicToEnglishNumbers(result);
  return result;
};

// تابع تبدیل کامل برای رمز عبور (اعداد فارسی/عربی به انگلیسی، بقیه کاراکترها حفظ می‌شوند)
const convertPasswordToEnglish = (str: string) => {
  if (!str) return str;
  // فقط اعداد را تبدیل می‌کنیم، حروف و سایر کاراکترها را دست نخورده نگه می‌داریم
  let result = persianToEnglishNumbers(str);
  result = arabicToEnglishNumbers(result);
  return result;
};

export default function LoginPage() {
  const router = useRouter();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [numFo, setNumFo] = useState(false);
  const [pasFo, setPasFo] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { data: session, status } = useSession();

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    // تبدیل اعداد فارسی/عربی به انگلیسی
    const englishValue = convertToEnglishNumbers(rawValue);
    // حذف هر کاراکتر غیر عددی
    const numericValue = englishValue.replace(/[^0-9]/g, '');
    setPhoneNumber(numericValue);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    // تبدیل اعداد فارسی/عربی در رمز عبور به انگلیسی
    const convertedValue = convertPasswordToEnglish(rawValue);
    setPassword(convertedValue);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // اطمینان از تبدیل مجدد اعداد (برای احتیاط بیشتر)
    const convertedPhone = convertToEnglishNumbers(phoneNumber);
    const convertedPassword = convertPasswordToEnglish(password);

    if (!convertedPhone || convertedPhone.length < 11) {
      toast.warn("شماره تلفن معتبر نیست");
      setLoading(false);
      return;
    }

    if (!convertedPassword || convertedPassword.length < 6) {
      toast.warn("رمز عبور باید حداقل ۶ کاراکتر باشد");
      setLoading(false);
      return;
    }

    try {
      const result = await signIn("credentials", {
        phoneNumber: convertedPhone,
        password: convertedPassword,
        redirect: false,
      });

      if (result?.error) {
        toast.warn("شماره موبایل یا رمز عبور اشتباه است");
      } else {
        toast.success("ورود با موفقیت انجام شد");
        router.push("/userPanel");
        router.refresh();
      }
    } catch (err) {
      toast.warn("خطا در ارتباط با سرور");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (status === "authenticated") {
      router.push("/userPanel");
    }
  }, [status, router]);

  return (
    <div className="ccdiv h-screen">
      <div className="w-[90%] md:w-[25%] p-5 rounded-xl shadow-[var(--yellowshadow)] bg-[var(--headerback)]/60">
        <Link
          href={'/'}
          className="flex items-center gap-3 justify-end tabBtn"
        >بازگشت <i className="ri-arrow-left-line"></i></Link>
        <h1 className="text-4xl font-bold text-center text-[var(--titleha)] mb-6">ورود</h1>
        <p className="text-center">فرم ورود به حساب کاربری شما</p>

        <form onSubmit={handleSubmit} className="space-y-4 mt-15">
          <div>
            <label className={`block text-sm font-medium mr-2 transition-all duration-300 ${numFo ? 'mb-0 -mt-8 text-[var(--title)]' : '-mb-9 mt-0'}`}>شماره تماس</label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={handlePhoneChange}
              className="w-full h-[50px] text-left px-3 border-b border-[var(--title)] outline-none"
              placeholder="09123456789"
              required
              onFocus={() => setNumFo(true)}
              onBlur={(e) => { if (e.target.value === '') { setNumFo(false) } }}
            />
          </div>

          <div className="mt-12 relative">
            <label className={`block text-sm font-medium mr-2 transition-all duration-300 ${pasFo ? 'mb-0 -mt-8 text-[var(--title)]' : '-mb-9 mt-0'}`}>رمز عبور</label>
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={handlePasswordChange}
              className="w-full h-[50px] text-left px-3 border-b border-[var(--title)] outline-none"
              placeholder="******"
              required
              onFocus={() => setPasFo(true)}
              onBlur={(e) => { if (e.target.value === '') { setPasFo(false) } }}
            />
            {password && (
              <i
                onClick={() => setShowPassword(!showPassword)}
                className={`absolute right-3 top-[45px] -translate-y-1/2 cursor-pointer ${showPassword ? "ri-eye-off-line" : "ri-eye-line"}`}
              ></i>
            )}
          </div>

          <Link
            href="/forgot-password"
            className="block text-sm text-left text-[var(--title)] hover:underline mt-4"
          >
            رمز عبور خود را فراموش کرده‌اید؟
          </Link>

          <button
            type="submit"
            disabled={loading}
            className="subBtnRe w-full h-[50px] mt-10"
          >
            {loading ? "در حال ورود..." : "ورود"}
          </button>
          <p
            className="text-sm text-center text--300"
          >این فرم مخصوص پرسنل کاری ما می باشد و فعلا از پذیرش شما معذوریم</p>
        </form>
      </div>
    </div>
  );
}